import userIdVerificationAdapter from '@/features/user/requests/adapters/user-id-verification-adapter';
import {
  type UserIdVerificationResponse,
  type UsersIdVerificationCollectionResponse,
} from '@/features/user/types/user-id-verification-response-types';
import { type UsersIdVerificationsCollection } from '@/features/user/types/user-id-verification-types';
import { type User } from '@/features/user/types/user-types';
import { logWarn } from '@/libs/logger/console-log';
import sendHttpError from '@/libs/logger/requests/send-http-error';
import createRequestSignal from '@/libs/request/create-request-signal';
import logUpstreamError from '@/libs/request/log-upstream-error';

const USER_VERIFICATION_CACHE_TTL_SECONDS = 300;

interface RedisHelpers {
  redisGet: (key: string) => Promise<{ value: unknown } | null>;
  redisSet: (key: string, value: unknown, expireInSeconds: number) => void;
}

async function getRedisHelpers(): Promise<RedisHelpers | null> {
  if (typeof window !== 'undefined' || 'EdgeRuntime' in globalThis) {
    return null;
  }

  const redisModule = await import('@/libs/redis/redis');

  return {
    redisGet: redisModule.redisGet,
    redisSet: redisModule.redisSet,
  };
}

function getUserVerificationCacheKey(userId: User['id']) {
  return `user-id-verification:${String(userId)}`;
}

function adaptCachedVerification(value: unknown) {
  if (value === null) {
    return null;
  }

  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return userIdVerificationAdapter(value as UserIdVerificationResponse);
}

interface UsersIdVerificationsParams {
  usersIds: User['id'][],
  next?: NextFetchRequestConfig,
}

export default async function getUsersIdVerifications(verificationsProps: UsersIdVerificationsParams): Promise<UsersIdVerificationsCollection> {
  const { next, usersIds } = verificationsProps;
  const uniqueUsersIds = [...new Set(usersIds)];
  const usersVerifications: UsersIdVerificationsCollection = {};
  const redisHelpers = await getRedisHelpers();

  if (uniqueUsersIds.length === 0) {
    return usersVerifications;
  }

  const cachedEntries = await Promise.all(uniqueUsersIds.map(async (userId) => ({
    cacheRecord: redisHelpers
      ? await redisHelpers.redisGet(getUserVerificationCacheKey(userId))
      : null,
    userId,
  })));

  const missingUsersIds: User['id'][] = [];

  for (const {
    cacheRecord,
    userId,
  } of cachedEntries) {
    if (!cacheRecord) {
      missingUsersIds.push(userId);
      continue;
    }

    const cachedVerification = adaptCachedVerification(cacheRecord.value);

    if (typeof cachedVerification === 'undefined') {
      missingUsersIds.push(userId);
      continue;
    }

    usersVerifications[userId] = cachedVerification;
  }

  if (missingUsersIds.length === 0) {
    return usersVerifications;
  }

  const urlPart = missingUsersIds.join(',');
  const url = `${process.env.NEXT_PUBLIC_API_URL}/v2/bgcheck/verification/${urlPart}?filter=public`;
  const headers: HeadersInit = {};

  let usersVerificationsResponse: Response;

  try {
    usersVerificationsResponse = await fetch(url, {
      headers,
      method: 'GET',
      signal: createRequestSignal(),
      ...(!next && { cache: 'no-store' }),
      next,
    });
  } catch (error) {
    // Timeout / network failure. Throttled + classified via the shared helper so a
    // burst (API down) can't flood stdout. Rethrown so the caller's handling is
    // unchanged (getUserById's withVerification path catches and degrades).
    logUpstreamError(error, {
      functionName: 'getUsersIdVerifications',
      url,
      extra: {
        requestedUsersCount: usersIds.length,
        uniqueUsersCount: uniqueUsersIds.length,
      },
    });

    throw error;
  }

  // TODO: rewrite to flow without break APP
  if (!usersVerificationsResponse.ok) {
    sendHttpError({
      baseName: 'Get users id verifications',
      functionName: 'getUsersIdVerifications',
      method: 'GET',
      statusCode: usersVerificationsResponse.status,
      url,
    });

    if (usersVerificationsResponse.status === 429) {
      logWarn('ID verification request was rate-limited', {
        requestedUsersCount: usersIds.length,
        uniqueUsersCount: uniqueUsersIds.length,
        url,
      });
    }

    throw new Error('Failed to fetch id verifications - getUsersIdVerifications');
  }

  const usersVerificationsResponseData = await usersVerificationsResponse.json() as UsersIdVerificationCollectionResponse;

  for (const [userId,
    userVerificationDetailsResponse] of Object.entries(
      usersVerificationsResponseData.data,
    )) {
    const numericUserId = Number(userId);

    usersVerifications[numericUserId] = userVerificationDetailsResponse
      ? userIdVerificationAdapter(userVerificationDetailsResponse)
      : null;

    redisHelpers?.redisSet(
      getUserVerificationCacheKey(numericUserId),
      userVerificationDetailsResponse,
      USER_VERIFICATION_CACHE_TTL_SECONDS,
    );
  }

  return usersVerifications;
}
